// Hook pour charger les détails d'une ressource Terraform depuis le schéma complet
import { useEffect, useState, useRef } from 'react';
import api from '../api/client';
import { getTerraformResourceName } from '../Config/resourceNameMapping';

export interface TerraformAttribute {
  name: string;
  type: string | string[]; // Peut être "string" ou ["map", "string"] par exemple
  required: boolean;
  optional?: boolean; // Propriété optionnelle (peut être définie par l'utilisateur)
  computed: boolean;
  description: string;
}

export interface TerraformBlock {
  name: string;
  nesting: string;
  minItems: number;
  maxItems: number;
  attributes: TerraformAttribute[];
}

export interface TerraformResourceDetail {
  resourceType: string;
  properties: TerraformAttribute[];
  nestedBlocks: TerraformBlock[];
  description: string;
}

/**
 * Hook pour charger les détails d'une ressource Terraform depuis le backend
 * Utilise le schéma Terraform complet (azurerm-schema.json)
 */
export function useTerraformResourceDetail(tfKey: string | null | undefined) {
  const [data, setData] = useState<TerraformResourceDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Pas de tfKey, on reset
    if (!tfKey) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Annuler la requête précédente si elle existe
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    // Convertir le nom d'affichage en vrai nom Terraform
    const terraformResourceName = getTerraformResourceName(tfKey);
    const url = `/api/resources/terraform/${encodeURIComponent(terraformResourceName)}`;

    api
      .get(url, { signal: ctrl.signal as any })
      .then((res) => {
        const j = res.data || {};
        const resourceDetail: TerraformResourceDetail = {
          resourceType: j.resourceType || terraformResourceName,
          properties: Array.isArray(j.properties) ? j.properties : [],
          nestedBlocks: Array.isArray(j.nestedBlocks) ? j.nestedBlocks : [],
          description: j.description || '',
        };
        setData(resourceDetail);
        setError(null);
      })
      .catch((e) => {
        if (e?.name === 'CanceledError' || e?.message === 'canceled') {
          return;
        }

        console.warn(`⚠️ Failed to load Terraform schema for ${tfKey} (mapped to ${terraformResourceName}):`, e.message);

        // Silently handle errors - fallback to manual mode
        const is404 = e?.response?.status === 404;
        if (is404) {
          setError('resource_not_found');
        } else {
          setError(e?.message || 'fetch_failed');
        }
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      ctrl.abort();
    };
  }, [tfKey]);

  return { data, loading, error };
}
